import type { AudioCue } from "./audioPort";

/**
 * The Starfall Wyrm's own sound set (M40; art/audio/v6/wyrm_sfx.py, docs/AUDIO.md "The Starfall Wyrm"): which cue each
 * of its moments plays and when, as pure rules so they are unit-tested in Node. level/wyrmAudio.ts plays them; the kill's
 * cry (render/effects.ts) and the crash (render/views/wyrmView.ts) are played where the picture knows the moment.
 *
 * Beats are where each file puts its event (wyrm_sfx.py): the dive's and the return's impact at 1.0 s (the spawn clip's
 * downbeat, the return's slam), a roar's voice 0.45 s in (after its inhale).
 */
export const WyrmCueBeats = {
  /** wyrm_dive / wyrm_return: the impact, on the clip's downbeat (WYRM_CLIPS.downbeat, WYRM_ARRIVAL.SlamMs). */
  impact: 1.0,
  /** wyrm_roar: where the voice starts in the file (its wind-up before it). */
  roarVoice: 0.45,
  /** wyrm_wingbeat: the downstroke's whoosh peak in the file. */
  wingPeak: 0.08,
} as const;

/** The `roar` clip's own roar starts here (art/unitkit/README.md: roar 0.5–1.17 s). */
export const RoarClipVoice = 0.5;

/** One cue of a moment: `delay` s from the moment (IAudio.play's delay). */
export interface WyrmCue { readonly cue: AudioCue; readonly delay: number }

/**
 * The arrival: the first time, the herald and the dive together (the dive's impact on the downbeat) and the roar
 * timed so its voice meets the spawn clip's roar (`roarAtS`, WYRM_ARRIVAL.RoarMs); a paid retry, the return (its slam on
 * the same downbeat, its own defiant roar after it) in place of all three.
 */
export function wyrmArrivalCues(retry: boolean, roarAtS: number): readonly WyrmCue[] {
  if (retry) return [{ cue: "wyrm_return", delay: 0 }];
  return [{ cue: "wyrm_herald", delay: 0 }, { cue: "wyrm_dive", delay: 0 }, { cue: "wyrm_roar", delay: Math.max(0, roarAtS - WyrmCueBeats.roarVoice) }];
}

/** A roar as it is hurt (75/50/25 %): the `roar` clip starts with the cue, so the voice is delayed onto the clip's. */
export const WyrmRoarCue: WyrmCue = { cue: "wyrm_roar", delay: Math.max(0, RoarClipVoice - WyrmCueBeats.roarVoice) };

/** The rest of its moments, one cue each. */
export const WYRM_CUE = {
  /** The kill: the crack and the anguished cry, on the killing round's landing (the fall's start). */
  kill: "wyrm_death",
  /** The fall's crash frame (WYRM_CLIPS.crash of the `die` clip, however slow the kill's slow motion makes it). */
  crash: "wyrm_crash",
  /** WyrmFled: the flee clip soars off. */
  flee: "wyrm_flee",
  /** The choice sheet opens. */
  choice: "wyrm_choice",
  /** The reveal card: a Starfall skin, or the hoard once the set is complete. */
  unlock: "wyrm_unlock",
  /** A hard blow (HurtStep of its health). */
  hurt: "wyrm_hit",
  /** A downstroke while it flies. */
  wing: "wyrm_wingbeat",
  /** The glitter around it while it lives. */
  glitter: "wyrm_shimmer",
} as const satisfies Record<string, AudioCue>;

/** Every cue the Wyrm plays (the manifest must ship each). */
export const WYRM_CUES: readonly AudioCue[] = ["wyrm_herald", "wyrm_dive", "wyrm_roar", "wyrm_return", ...Object.values(WYRM_CUE)];

/**
 * Its health is read each tick; a glassy crack sounds each time it has lost another `HurtStep` of its full health since
 * the last one (so a heavy blow rings at once and a stream of archer arrows now and then), never on a heal or a retry's
 * reset. CUE_MIX's gap holds them to two a second.
 */
export const HurtStep = 0.04;

export class WyrmHurtGate {
  private last = -1;
  /** Whether this health fraction (0..1) has fallen another step since the last crack. `reset` on a new appearance. */
  feed(frac: number): boolean {
    if (this.last < 0 || frac > this.last) { this.last = frac; return false; }
    if (this.last - frac < HurtStep) return false;
    this.last = frac;
    return true;
  }
  reset(): void { this.last = -1; }
}

/**
 * The wingbeat: once a flight loop, a little before the first downstroke's peak (anim_wyrm.py: two beats a loop, each
 * downstroke the first 40 % of its beat, fastest at a fifth of it — loop phase 0.1), less the file's own lead to its
 * peak, so the whoosh lands on the stroke. Phases are of the clip's loop (AnimRig.loopPhase), lengths in s.
 */
export const WING_LOOPS: Readonly<Record<string, { readonly length: number; readonly peak: number }>> = {
  walk: { length: 1.2, peak: 0.1 },        // the loops start at 0 since 2026-10-24 (were 1.233 / 2.033 with a held first frame)
  idle: { length: 2.0, peak: 0.1 },
};

/** The loop phase at which a wingbeat is asked for in `clip`, or null for a clip with no beat to follow. */
export function wingTrigger(clip: string): number | null {
  const l = WING_LOOPS[clip];
  return l ? l.peak - WyrmCueBeats.wingPeak / l.length : null;
}

/** Whether the loop crossed its wingbeat between two frames' phases (wrapping round the loop's end). */
export function wingCrossed(clip: string, before: number, after: number): boolean {
  const at = wingTrigger(clip);
  if (at === null || before < 0 || after < 0) return false;
  return before <= after ? before < at && after >= at : before < at || after >= at;
}

/** The glitter: one now and then while it lives, `GlitterGap` s apart with up to `GlitterJitter` s more at random. */
export const GlitterGap = 3.2;
export const GlitterJitter = 2.0;
export function nextGlitter(r: number): number { return GlitterGap + GlitterJitter * Math.max(0, Math.min(1, r)); }
